import { MouseEvent, useMemo, useState } from "react";
import { Accordion, Badge, Button, Center, Group, Loader, Paper, SimpleGrid, Stack, Text } from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconCheck, IconExternalLink } from "@tabler/icons-react";
import moment from "moment";

import { getAge, getGender, getParticipant, getParticipantName, getParticipantNameReversed } from "../../functions/programregistration.functions";
import { ChildParticipant, ProgramRegistration } from "../../models/programregistration.models";
import { ProgramLeaveType } from "../../models/programs.models";
import { useProgramSessionEnrollmentCheckInMutation, useProgramSessionEnrollmentCheckOutMutation, ProgramSessionEnrollmentRegistration } from "../../api/programsession.api";
import ParentGuardianContactModal from "./ParentGuardianContactModal";
import ParticipantInfoModal from "./ParticipantInfoModal";
import classes from "./ProgramSessionCheckIn.module.css";

interface ProgramSessionCheckInTableProps {
	enrollments: ProgramSessionEnrollmentRegistration[];
	isLoading: boolean;
	programSessionId: number;
}

interface ProgramSessionCheckInParentGuardian {
	parentGuardianId: string;
	name: string;
	location: string;
	mobilePhone: string;
	emergencyContact: string;
}

interface ProgramSessionCheckInRow {
	id: string;
	registrationId: number;
	enrollmentId: number;
	enrollmentDate: string;
	waitlist: boolean;
	checkedIn: boolean;
	checkedOut: boolean;
	canLeave: boolean;
	programCanLeave: ProgramLeaveType;
	participantName: string;
	subtitle: string;
	needsSupport: string;
	healthConcerns: string;
	allergies: string;
	specialNeeds: string;
	parentGuardians: ProgramSessionCheckInParentGuardian[];
}

const ProgramSessionCheckIn = ({ enrollments, isLoading, programSessionId }: ProgramSessionCheckInTableProps) => {
	const [openItems, setOpenItems] = useState<string[]>([]);
	const [selectedParentGuardian, setSelectedParentGuardian] = useState<{
		registrationId: number;
		parentGuardianId: string;
		name: string;
		location: string;
		mobilePhone: string;
		emergencyContact: string;
	} | null>(null);
	const [selectedParticipantInfo, setSelectedParticipantInfo] = useState<{
		registration: ProgramRegistration | null;
		participantId: string | undefined;
		participantName: string | undefined;
		healthConcerns: string | undefined;
		allergies: string | undefined;
		specialNeeds: string | undefined;
	} | null>(null);
	const checkInMutation = useProgramSessionEnrollmentCheckInMutation(programSessionId);
	const checkOutMutation = useProgramSessionEnrollmentCheckOutMutation(programSessionId);

	const toggleCheckIn = (programSessionEnrollmentId: number, checkedIn: boolean) => {
		checkInMutation.mutate({ programSessionEnrollmentId, checkedIn });
	}

	const toggleCheckInWithCanLeave = (programSessionEnrollmentId: number, checkedIn: boolean, canLeave?: boolean) => {
		checkInMutation.mutate({ programSessionEnrollmentId, checkedIn, canLeave });
	}

	const toggleCheckOut = (programSessionEnrollmentId: number, checkedOut: boolean) => {
		checkOutMutation.mutate({ programSessionEnrollmentId, checkedOut });
	}

	const onCheckInClick = (event: MouseEvent, row: { id: string, enrollmentId: number, checkedIn: boolean, participantName: string, programCanLeave: ProgramLeaveType }) => {
		event.stopPropagation();

		if (!row.checkedIn) {
			if (row.programCanLeave === ProgramLeaveType.AskPermission) {
				modals.openConfirmModal({
					title: <Text size="lg" fw="bold">Leave Permission</Text>,
					children: <Text size="sm">Can {row.participantName || "this participant"} leave on their own, or does a parent/guardian need to pick them up?</Text>,
					labels: { confirm: "Can Leave On Own", cancel: "Parent Must Pick Up" },
					confirmProps: { color: "green", fullWidth: true },
					cancelProps: { color: "blue", variant: "filled", fullWidth: true },
					onConfirm: () => {
						toggleCheckInWithCanLeave(row.enrollmentId, true, true);
						setOpenItems((prev) => prev.filter((item) => item !== row.id));
					},
					onCancel: () => {
						toggleCheckInWithCanLeave(row.enrollmentId, true, false);
						setOpenItems((prev) => prev.filter((item) => item !== row.id));
					}
				});
				return;
			}

			toggleCheckIn(row.enrollmentId, true);
			setOpenItems((prev) => prev.filter((item) => item !== row.id));
			return;
		}

		modals.openConfirmModal({
			title: <Text size="lg" fw="bold">Undo Check In</Text>,
			children: <Text size="sm">Undo check in for {row.participantName || "this participant"}?</Text>,
			labels: { confirm: "Undo Check In", cancel: "Cancel" },
			confirmProps: { color: "red" },
			cancelProps: { variant: "subtle" },
			onConfirm: () => {
				toggleCheckIn(row.enrollmentId, false);
				setOpenItems((prev) => prev.filter((item) => item !== row.id));
			},
		});
	}

	const onUpdateContactClick = (event: MouseEvent, row: ProgramSessionCheckInRow, parentGuardian: ProgramSessionCheckInParentGuardian) => {
		event.stopPropagation();

		if (!parentGuardian.parentGuardianId) {
			return;
		}

		setSelectedParentGuardian({
			registrationId: row.registrationId,
			parentGuardianId: parentGuardian.parentGuardianId,
			name: parentGuardian.name,
			location: parentGuardian.location,
			mobilePhone: parentGuardian.mobilePhone,
			emergencyContact: parentGuardian.emergencyContact,
		});
	}

	const onUpdateParticipantInfoClick = (event: MouseEvent, row: ProgramSessionCheckInRow, registration: ProgramRegistration | undefined, participantId: string | undefined) => {
		event.stopPropagation();

		if (!registration || !participantId) {
			return;
		}

		setSelectedParticipantInfo({
			registration,
			participantId,
			participantName: row.participantName,
			healthConcerns: row.healthConcerns,
			allergies: row.allergies,
			specialNeeds: row.specialNeeds,
		});
	}

	const onCheckOutClick = (event: MouseEvent, row: { id: string, enrollmentId: number, checkedOut: boolean, participantName: string }) => {
		event.stopPropagation();

		if (!row.checkedOut) {
			toggleCheckOut(row.enrollmentId, true);
			setOpenItems((prev) => prev.filter((item) => item !== row.id));
			return;
		}

		modals.openConfirmModal({
			title: <Text size="lg" fw="bold">Undo Check Out</Text>,
			children: <Text size="sm">Undo check out for {row.participantName || "this participant"}?</Text>,
			labels: { confirm: "Undo Check Out", cancel: "Cancel" },
			confirmProps: { color: "red" },
			cancelProps: { variant: "subtle" },
			onConfirm: () => {
				toggleCheckOut(row.enrollmentId, false);
				setOpenItems((prev) => prev.filter((item) => item !== row.id));
			},
		});
	}

	const displayValue = (value?: string | null) => {
		if (!value || value.trim() === "") {
			return "N/A";
		}

		return value;
	}

	const rows = useMemo(() => {
		return enrollments
			.map((enrollment) => {
				const participant = getParticipant(enrollment.Registration, enrollment.ProgramSessionEnrollment.ParticipantId);
				const participantName = getParticipantNameReversed(enrollment.Registration, enrollment.ProgramSessionEnrollment.ParticipantId);
				const age = getAge(participant as ChildParticipant, enrollment.Registration.DateFrom) ?? "Adult";
				const gender = participant ? getGender(participant) : "Unknown";
				const childParticipant = participant as ChildParticipant | undefined;
				const parentGuardians = enrollment.Registration.Data.ParentGuardians ?? [];

				return {
					id: `${enrollment.Registration.Id}-${enrollment.ProgramSessionEnrollment.ParticipantId}`,
					registrationId: enrollment.Registration.Id,
					enrollmentId: enrollment.ProgramSessionEnrollment.Id,
					enrollmentDate: enrollment.ProgramSessionEnrollment.EnrollmentDate,
					waitlist: enrollment.ProgramSessionEnrollment.WaitList === true,
					checkedIn: enrollment.ProgramSessionEnrollment.CheckedIn === true,				
					checkedOut: enrollment.ProgramSessionEnrollment.CheckedOut === true,					
					participantName,
					canLeave: enrollment.ProgramSessionEnrollment.CanLeave === true,
					programCanLeave: enrollment.ProgramCanLeave,
					subtitle: `Age ${age}, ${gender}`,
					needsSupport: childParticipant?.NeedsSupport === true ? "Yes" : "No",
					healthConcerns: childParticipant?.HealthConcerns,
					allergies: childParticipant?.Allergies,
					specialNeeds: childParticipant?.SpecialNeeds,
					parentGuardians: parentGuardians.map((parentGuardian) => ({
						parentGuardianId: parentGuardian.Id,
						name: `${parentGuardian.FirstName ?? ""} ${parentGuardian.LastName ?? ""}`.trim() || "(unavailable)",
						location: parentGuardian.Location ?? "",
						mobilePhone: parentGuardian.MobilePhone ?? "",
						emergencyContact: parentGuardian.EmergencyContact ?? "",
					})),
				} as ProgramSessionCheckInRow;
			})
			.sort((left, right) => {
				const statusOrder = (row: { checkedIn: boolean, checkedOut: boolean }) => {
					if (!row.checkedIn) return 0;
					if (!row.checkedOut) return 1;
					return 2;
				};
				const statusDiff = statusOrder(left) - statusOrder(right);
				if (statusDiff !== 0) return statusDiff;
				return left.participantName.trim().localeCompare(right.participantName.trim());
			});
	}, [enrollments]);

	const groupedRows = useMemo(() => {
		const waitlistRows = rows
			.filter((row) => row.waitlist)
			.sort((left, right) => {
				const leftDate = left.enrollmentDate ? new Date(left.enrollmentDate).getTime() : Number.MAX_SAFE_INTEGER;
				const rightDate = right.enrollmentDate ? new Date(right.enrollmentDate).getTime() : Number.MAX_SAFE_INTEGER;
				const dateDiff = leftDate - rightDate;
				if (dateDiff !== 0) {
					return dateDiff;
				}
				return left.participantName.trim().localeCompare(right.participantName.trim());
			});

		return {
			enrolledRows: rows.filter((row) => !row.waitlist),
			waitlistRows,
		};
	}, [rows]);

	if (isLoading) {
		return <Center h={150} mx="auto">
			<Loader />
		</Center>;
	}

	if (rows.length === 0) {
		return <Paper p="md">
			<Text c="dimmed">No participants found for this session.</Text>
		</Paper>;
	}

	const renderEnrollmentAccordion = (groupRows: ProgramSessionCheckInRow[]) => {
		if (groupRows.length === 0) {
			return null;
		}

		return <Accordion multiple radius="md" variant="default" chevronPosition="left" classNames={{ chevron: classes.chevron, control: classes.control }} value={openItems} onChange={setOpenItems}>
			{groupRows.map((row) => {
				return <Accordion.Item value={row.id} key={row.id}>
					<Accordion.Control component="div">
						<Group justify="space-between" align="flex-start" gap={0} wrap="nowrap" w="100%">
							<Group align="start" wrap="nowrap" mr="xs">
								<Stack gap={0}>
									<Text fw={600}>{row.participantName || "(unavailable)"}</Text>
									<Group gap={4} align="center">
										<Text size="sm" c="dimmed">{row.subtitle}</Text>
										{row.checkedIn && row.programCanLeave === ProgramLeaveType.AskPermission && (
											<Badge size="sm" variant="light" color={row.canLeave ? "green" : "blue"}>
												{row.canLeave ? "Can leave on own" : "Parent / Guardian pick up"}
											</Badge>
										)}
									</Group>
								</Stack>
								<Stack gap={0}>
									<Stack align="flex-start">
										<Button
											variant="light"
											radius="md"
											size="compact-sm"
											component="a"
											href={`/registrations/view/${row.registrationId}`}
											target="_blank"
											rel="noopener noreferrer"
											rightSection={<IconExternalLink size={16} />}
										>
											Registration
										</Button>
									</Stack>
									<Stack align="flex-start">
										{row.waitlist && <Text fz="sm" c="dimmed">Enrolled: {moment.utc(row.enrollmentDate).format("MMMM D, YYYY h:mm a")}</Text>}
									</Stack>
								</Stack>
							</Group>
							<Group gap="xs" wrap="wrap" align="flex-start" justify="flex-end">
								{!row.checkedOut && (
								<Button
									variant={row.checkedIn ? "light" : "filled"}
									color={row.checkedIn ? "green" : undefined}
									size="md"
									radius="lg"
									onClick={(event) => onCheckInClick(event, row)}
									loading={checkInMutation.isLoading}
									leftSection={row.checkedIn ? <IconCheck size={20} stroke={3} /> : null}
								>
									{row.checkedIn ? "Checked In" : "Check In"}
								</Button>
								)}
								{row.checkedIn && (
									<Button
										variant={row.checkedOut ? "light" : "filled"}
										color={row.checkedOut ? "green" : undefined}
										size="md"
										radius="lg"
										onClick={(event) => onCheckOutClick(event, row)}
										loading={checkOutMutation.isLoading}
										leftSection={row.checkedOut ? <IconCheck size={20} stroke={3} /> : null}
									>
										{row.checkedOut ? "Checked Out" : "Check Out"}
									</Button>
								)}
							</Group>
						</Group>
					</Accordion.Control>
					<Accordion.Panel>
						<Stack gap={8} mt="xs">
							<SimpleGrid cols={{ xs: 2, md: 3 }} spacing="md">
								<Paper bg="var(--mantine-color-default-hover)" withBorder p="md" radius="md">
									<Stack gap={2} mb="xs">
										<Text size="sm" fw={600}>Participant Details</Text>
										{row.needsSupport === "Yes" && <Badge color="green" variant="filled" radius="sm">One-on-One Support Required</Badge>}
										<Text size="sm">Health concerns: {displayValue(row.healthConcerns)}</Text>
										<Text size="sm">Allergies: {displayValue(row.allergies)}</Text>
										<Text size="sm">Special needs: {displayValue(row.specialNeeds)}</Text>
									</Stack>
									<Group>
										<Button
											variant="light"
											onClick={(event) => {
												const enrollment = enrollments.find((item) => item.ProgramSessionEnrollment.Id === row.enrollmentId);
												onUpdateParticipantInfoClick(event, row, enrollment?.Registration ?? undefined, enrollment?.ProgramSessionEnrollment.ParticipantId);
											}}
										>
											Update Details
										</Button>
									</Group>
								</Paper>
								{row.parentGuardians.length === 0 && <>
									<Paper bg="var(--mantine-color-default-hover)" withBorder p="md" radius="md">
										<Stack gap={2}>
											<Text size="sm" fw={600}>Parent / Guardian</Text>
											<Text size="sm">No parent / guardian information available.</Text>
										</Stack>
									</Paper>
								</>}
								{row.parentGuardians.map((parentGuardian, index) => {
									return <Paper key={`${row.id}-guardian-${index}`} bg="var(--mantine-color-default-hover)" withBorder p="md" radius="md">
										<Stack gap={2} mb="xs">
											<Text size="sm" fw={600}>Parent / Guardian</Text>
											<Stack key={`${row.id}-guardian-${index}`} gap={0}>
												<Text size="sm">
													{parentGuardian.name}
												</Text>
												<Text size="sm">Location: {displayValue(parentGuardian.location)}</Text>
												<Text size="sm">Cell Phone: {displayValue(parentGuardian.mobilePhone)}</Text>
												<Text size="sm">Emergency Contact: {displayValue(parentGuardian.emergencyContact)}</Text>
											</Stack>
										</Stack>
										<Button variant="light" onClick={(event) => onUpdateContactClick(event, row, parentGuardian)} disabled={!parentGuardian.parentGuardianId}>Update Contact</Button>
									</Paper>
								})}
							</SimpleGrid>
						</Stack>
					</Accordion.Panel>
				</Accordion.Item>;
			})}
		</Accordion>;
	};

	return <>
		{groupedRows.enrolledRows.length > 0 && <Stack gap="xs">
			<Group gap="xs" align="center">
				<Text fw={700} size="sm">Enrolled</Text>
				<Badge size="sm" variant="light" color="blue">{groupedRows.enrolledRows.length}</Badge>
			</Group>
			{renderEnrollmentAccordion(groupedRows.enrolledRows)}
		</Stack>}
		{groupedRows.waitlistRows.length > 0 && <Stack gap="xs" my="md">
			<Group gap="xs" align="center">
				<Text fw={700} size="sm" c="yellow.8">Waitlist</Text>
				<Badge size="sm" variant="light" color="yellow">{groupedRows.waitlistRows.length}</Badge>
			</Group>
			{renderEnrollmentAccordion(groupedRows.waitlistRows)}
		</Stack>}
		
		<ParentGuardianContactModal
			opened={selectedParentGuardian != null}
			onClose={() => setSelectedParentGuardian(null)}
			programSessionId={programSessionId}
			registrationId={selectedParentGuardian?.registrationId}
			parentGuardianId={selectedParentGuardian?.parentGuardianId}
			parentGuardianName={selectedParentGuardian?.name}
			location={selectedParentGuardian?.location}
			mobilePhone={selectedParentGuardian?.mobilePhone}
			emergencyContact={selectedParentGuardian?.emergencyContact}
		/>
		<ParticipantInfoModal
			opened={selectedParticipantInfo != null}
			onClose={() => setSelectedParticipantInfo(null)}
			programSessionId={programSessionId}
			registration={selectedParticipantInfo?.registration ?? null}
			participantId={selectedParticipantInfo?.participantId}
			participantName={selectedParticipantInfo?.participantName}
			healthConcerns={selectedParticipantInfo?.healthConcerns}
			allergies={selectedParticipantInfo?.allergies}
			specialNeeds={selectedParticipantInfo?.specialNeeds}
		/>
		{/* <ParticipantInfoModal
			opened={selectedParticipantInfo != null}
			onClose={() => setSelectedParticipantInfo(null)}
			programSessionId={programSessionId}
			registration={selectedParticipantInfo?.registration ?? null}
			participantId={selectedParticipantInfo?.participantId}
			participantName={selectedParticipantInfo?.participantName}
			healthConcerns={selectedParticipantInfo?.healthConcerns}
			allergies={selectedParticipantInfo?.allergies}
			specialNeeds={selectedParticipantInfo?.specialNeeds}
		/> */}
	</>
}

export default ProgramSessionCheckIn;